import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { usePasswordReset, useSignInFlow } from '@mystudioclass/auth';
import { Video, ArrowLeft, Mail, Lock, CheckCircle, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ForgotPasswordPage() {
  // Force light mode on auth pages
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
  }, []);
  const { isLoaded, sendResetCode, resetPassword, error } = usePasswordReset();
  const { createSignIn } = useSignInFlow();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if we have a reset token from the URL (user clicked link in email)
  const resetToken = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [step, setStep] = useState(resetToken ? 'password' : 'email'); // 'email', 'sent', 'password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    if (!isLoaded || !email) return;

    setLoading(true);
    try {
      await sendResetCode(email);
      toast.success('Reset link sent! Check your email.');
      setStep('sent');
    } catch (err) {
      console.error('Send reset email error:', err);
      const errorMessage = err.message || error?.message || 'Failed to send reset email';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!isLoaded || !password || !resetToken) return;

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(password, resetToken);
      toast.success('Password reset successfully!');

      // Auto sign-in with new password if we have the email
      if (emailFromUrl) {
        try {
          await createSignIn({ identifier: emailFromUrl, password });
          // Use hard navigation to avoid race condition with React auth state
          window.location.href = '/dashboard';
          return;
        } catch (signInErr) {
          console.error('Auto sign-in failed:', signInErr);
          // Fall through to login page if auto sign-in fails
        }
      }

      window.location.href = '/sign-in';
    } catch (err) {
      console.error('Reset password error:', err);
      const errorMessage = err.message || error?.message || 'Failed to reset password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-base)' }}>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="https://mystudioclass.com" className="inline-block">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4 transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))' }}
            >
              <Video className="w-8 h-8" style={{ color: 'var(--bg-base)' }} />
            </div>
          </a>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>My Studio Class</h1>
          <p className="mt-2" style={{ color: 'var(--text-muted)' }}>Reset Your Password</p>
        </div>

        {/* Form Card */}
        <div className="rounded-2xl shadow-xl p-8" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
          {/* Email Step - Request Reset */}
          {step === 'email' && (
            <form onSubmit={handleSendResetEmail} className="space-y-6">
              <div className="flex items-center justify-center mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 text-[var(--text-secondary)]">
                  <Mail className="w-6 h-6" />
                </div>
              </div>

              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="admin@yourstudio.com"
                  required
                  disabled={!isLoaded || loading}
                />
              </div>

              <button
                type="submit"
                disabled={!isLoaded || loading}
                className="btn btn-primary w-full"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          {/* Sent Step - Show Success */}
          {step === 'sent' && (
            <div className="space-y-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600">
                  <CheckCircle className="w-6 h-6" />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Check Your Email</h3>
                <p className="text-sm text-gray-600">
                  We sent a password reset link to <strong>{email}</strong>.
                  Click the link in the email to reset your password.
                </p>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500 mb-4">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <button
                  onClick={() => setStep('email')}
                  className="text-sm text-[var(--text-secondary)] hover:text-primary-700 font-medium"
                >
                  Try again with different email
                </button>
              </div>
            </div>
          )}

          {/* Password Step - User Clicked Link in Email */}
          {step === 'password' && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="flex items-center justify-center mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 text-[var(--text-secondary)]">
                  <Lock className="w-6 h-6" />
                </div>
              </div>

              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">
                  Enter your new password below.
                </p>
              </div>

              <div>
                <label className="label">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pr-10"
                    placeholder="••••••••"
                    required
                    disabled={!isLoaded || loading}
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="label">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input pr-10"
                    placeholder="••••••••"
                    required
                    disabled={!isLoaded || loading}
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={!isLoaded || loading}
                className="btn btn-primary w-full"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/sign-in"
              className="inline-flex items-center text-sm text-[var(--text-secondary)] hover:text-primary-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
